import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Plus, 
  Filter, 
  Users, 
  Building, 
  Edit2, 
  Trash2, 
  Shield,
  ChevronLeft, 
  ChevronRight, 
  Download,
  UserCheck,
  UserX,
  Settings,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Clock,
  Mail
} from 'lucide-react';
import { FaGoogle } from 'react-icons/fa';
import { db, doc, deleteDoc, writeBatch } from '../../firebase';
import CreateUserForm from './CreateUserForm';
import WorkspaceSync from './WorkspaceSync';
import UserModals from './UserModals';
import ModalWrapper from './ModalWrapper';
import styles from '../CSS/UserManagement.module.css';

const UserManagement = ({ users, roles, currentUser, onUpdate, showFeedback }) => {
  // Estados para filtros y búsqueda
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedOrgUnit, setSelectedOrgUnit] = useState('');
  const [showCreateUser, setShowCreateUser] = useState(false);
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Estados para modales
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Estados para acciones en lote
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showWorkspaceSync, setShowWorkspaceSync] = useState(false);
  const [bulkRoleChange, setBulkRoleChange] = useState('');
  const [showBulkRoleModal, setShowBulkRoleModal] = useState(false);
  
  // Estados para workspace
  const [workspaceUsers, setWorkspaceUsers] = useState([]);

  // Obtener unidades organizativas únicas de usuarios locales
  const getUniqueOrgUnits = () => {
    const localOrgUnits = users
      .filter(user => user.orgUnit)
      .map(user => ({
        path: user.orgUnit,
        name: user.orgUnitName || user.orgUnit.split('/').pop()
      }))
      .reduce((acc, current) => {
        const exists = acc.find(item => item.path === current.path);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);

    const allOrgUnits = [...localOrgUnits]
      .sort((a, b) => a.name.localeCompare(b.name));
    
    return allOrgUnits;
  };

  // Combinar usuarios locales con usuarios de Workspace para la vista unificada
  const getCombinedUsers = () => {
    const combinedUsers = [...users];
    
    // Agregar usuarios de Workspace que no están en la base de datos local
    workspaceUsers.forEach(wsUser => {
      if (!users.find(u => u.id === wsUser.primaryEmail)) {
        combinedUsers.push({
          id: wsUser.primaryEmail,
          firstName: wsUser.name?.givenName || '',
          lastName: wsUser.name?.familyName || '',
          role: wsUser.localRole,
          orgUnit: wsUser.orgUnitPath,
          orgUnitName: wsUser.orgUnitName,
          syncedFromWorkspace: false,
          workspaceId: wsUser.id,
          workspaceCreated: wsUser.creationTime,
          suspended: wsUser.suspended || false,
          isWorkspaceAvailable: true,
          workspaceData: {
            primaryEmail: wsUser.primaryEmail,
            recoveryEmail: wsUser.recoveryEmail,
            isAdmin: wsUser.isAdmin || false,
            isDelegatedAdmin: wsUser.isDelegatedAdmin || false,
            lastLoginTime: wsUser.lastLoginTime
          }
        });
      }
    });

    return combinedUsers;
  };

  const allOrgUnits = getUniqueOrgUnits();
  const combinedUsers = getCombinedUsers();

  // Filtrar usuarios
  const filteredUsers = combinedUsers.filter(user => {
    const matchesSearch = `${user.firstName} ${user.lastName} ${user.id}`.toLowerCase()
      .includes(searchQuery.toLowerCase());
    
    const matchesRole = !selectedRole || user.role === selectedRole;
    
    const matchesSource = !selectedSource || 
      (selectedSource === 'workspace' && user.syncedFromWorkspace) ||
      (selectedSource === 'manual' && !user.syncedFromWorkspace && !user.isWorkspaceAvailable) ||
      (selectedSource === 'available' && user.isWorkspaceAvailable && !user.syncedFromWorkspace);
    
    const matchesOrgUnit = !selectedOrgUnit || user.orgUnit === selectedOrgUnit;
    
    return matchesSearch && matchesRole && matchesSource && matchesOrgUnit;
  });

  // Paginación
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const indexOfLastUser = currentPage * itemsPerPage;
  const indexOfFirstUser = indexOfLastUser - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  // Estadísticas
  const getStats = () => {
    const total = combinedUsers.length;
    const local = users.length;
    const workspaceLocal = users.filter(u => u.syncedFromWorkspace).length;
    const manualUsers = users.filter(u => !u.syncedFromWorkspace).length;
    const availableInWorkspace = workspaceUsers.filter(wu => !users.find(u => u.id === wu.primaryEmail)).length;
    const adminUsers = combinedUsers.filter(u => u.role === 'admin').length;
    const suspendedUsers = combinedUsers.filter(u => u.suspended).length;

    return { total, local, workspaceLocal, manualUsers, availableInWorkspace, adminUsers, suspendedUsers };
  };

  const stats = getStats();

  // Funciones de utilidad
  const getRoleName = (roleId) => {
    if (roleId === 'admin') return 'Administrador';
    if (roleId === 'user') return 'Usuario';
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : roleId;
  };

  const canDeleteUser = (userEmail) => {
    return userEmail !== currentUser?.email;
  };

  const isCurrentUser = (userEmail) => {
    return userEmail === currentUser?.email;
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Nunca';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    if (diffDays < 30) return `Hace ${Math.floor(diffDays / 7)} semanas`;
    if (diffDays < 365) return `Hace ${Math.floor(diffDays / 30)} meses`;
    return `Hace ${Math.floor(diffDays / 365)} años`;
  };

  // Manejo de selecciones
  const toggleUserSelection = (email) => {
    setSelectedUsers(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(email)) {
        newSelected.delete(email);
      } else {
        newSelected.add(email);
      }
      return newSelected;
    });
  };

  const toggleAllUsers = (select) => {
    setSelectedUsers(prev => {
      const newSelected = new Set(prev);
      currentUsers.forEach(user => {
        if (select) {
          newSelected.add(user.id);
        } else {
          newSelected.delete(user.id);
        }
      });
      return newSelected;
    });
  };

  // Cambiar rol en lote
  const handleBulkRoleChange = async () => {
    if (selectedUsers.size === 0) {
      showFeedback("warning", "Seleccione usuarios para cambiar el rol");
      return;
    }

    if (!bulkRoleChange) {
      showFeedback("warning", "Seleccione un rol");
      return;
    }

    // Filtrar solo usuarios que están en la BD local y excluir el usuario actual
    const usersToUpdate = Array.from(selectedUsers).filter(email => {
      const user = combinedUsers.find(u => u.id === email);
      return user && !user.isWorkspaceAvailable && !isCurrentUser(email);
    });

    if (usersToUpdate.length === 0) {
      showFeedback("warning", "No hay usuarios válidos para actualizar en la base de datos (se excluye tu usuario actual)");
      return;
    }

    // Verificar si se está intentando cambiar el rol del usuario actual
    if (selectedUsers.has(currentUser?.email)) {
      showFeedback("info", `Se actualizarán ${usersToUpdate.length} usuarios (tu cuenta actual se omite por seguridad)`);
    }

    try {
      const batch = writeBatch(db);
      let updatedCount = 0;

      for (const email of usersToUpdate) {
        const userRef = doc(db, 'users', email);
        batch.update(userRef, { role: bulkRoleChange });
        updatedCount++;
      }

      await batch.commit();
      
      setSelectedUsers(new Set());
      setBulkRoleChange('');
      setShowBulkRoleModal(false);
      showFeedback("success", `Rol actualizado para ${updatedCount} usuarios`);
      onUpdate();

    } catch (error) {
      console.error('Error updating users:', error);
      showFeedback("error", "Error al actualizar usuarios: " + error.message);
    }
  };

  // Eliminar usuarios en lote (solo de Firebase)
  const bulkDeleteUsers = async () => {
    if (selectedUsers.size === 0) {
      showFeedback("warning", "Seleccione usuarios para eliminar");
      return;
    }

    // Filtrar solo usuarios que están en la BD local
    const usersToDelete = Array.from(selectedUsers).filter(email => {
      const user = combinedUsers.find(u => u.id === email);
      return user && !user.isWorkspaceAvailable && canDeleteUser(email);
    });

    if (usersToDelete.length === 0) {
      showFeedback("warning", "No hay usuarios válidos para eliminar de la base de datos");
      return;
    }

    if (!window.confirm(`¿Está seguro de que desea eliminar ${usersToDelete.length} usuarios de la base de datos? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      const batch = writeBatch(db);
      let deletedCount = 0;

      for (const email of usersToDelete) {
        const userRef = doc(db, 'users', email);
        batch.delete(userRef);
        deletedCount++;
      }

      await batch.commit();
      
      setSelectedUsers(new Set());
      showFeedback("success", `${deletedCount} usuarios eliminados de la base de datos`);
      onUpdate();

    } catch (error) {
      console.error('Error deleting users:', error);
      showFeedback("error", "Error al eliminar usuarios: " + error.message);
    }
  };

  // Manejo de éxito de creación/edición
  const handleCreateSuccess = () => {
    setShowCreateUser(false);
    onUpdate();
    showFeedback("success", "Usuario creado exitosamente");
  };

  const handleEditSuccess = () => {
    setEditingUser(null);
    onUpdate();
    showFeedback("success", "Usuario actualizado exitosamente");
  };

  // Renderizado de paginación
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const showPages = 5;
    const startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    const endPage = Math.min(totalPages, startPage + showPages - 1);

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className={styles.pagination}>
        <button
          className={`${styles.pageButton} ${currentPage === 1 ? styles.disabled : ''}`}
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          <ChevronLeft size={18} />
        </button>
        
        {startPage > 1 && (
          <>
            <button
              className={styles.pageButton}
              onClick={() => setCurrentPage(1)}
            >
              1
            </button>
            {startPage > 2 && <span className={styles.ellipsis}>...</span>}
          </>
        )}
        
        {pageNumbers.map(page => (
          <button
            key={page}
            className={`${styles.pageButton} ${currentPage === page ? styles.active : ''}`}
            onClick={() => setCurrentPage(page)}
          >
            {page}
          </button>
        ))}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className={styles.ellipsis}>...</span>}
            <button
              className={styles.pageButton}
              onClick={() => setCurrentPage(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}
        
        <button
          className={`${styles.pageButton} ${currentPage === totalPages ? styles.disabled : ''}`}
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          <ChevronRight size={18} />
        </button>
      </div>
    );
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedRole, selectedSource, selectedOrgUnit]);

  return (
    <div className={styles.container}>
      {/* Panel de sincronización con Workspace como modal */}
      <ModalWrapper 
        isOpen={showWorkspaceSync} 
        onClose={() => setShowWorkspaceSync(false)}
        className="medium"
      >
        <WorkspaceSync
          users={users}
          currentUser={currentUser}
          onUpdate={onUpdate}
          showFeedback={showFeedback}
          isVisible={true}
          onToggle={() => setShowWorkspaceSync(false)}
          isModal={true}
          roles={roles}
        />
      </ModalWrapper>

      {/* Filtros sin estadísticas */}
      <div className={styles.filters}>
        <div className={styles.searchContainer}>
          <Search size={20} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Buscar por nombre, apellido o email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
          {searchQuery && (
            <button
              className={styles.clearSearchButton}
              onClick={() => setSearchQuery('')}
            >
              ×
            </button>
          )}
        </div>
        
        <div className={styles.filterGroup}>
          <div className={styles.filterContainer}>
            <label className={styles.filterLabel}>
              <Shield size={16} />
              Rol
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">Todos los roles</option>
              <option value="admin">Administrador</option>
              <option value="user">Usuario</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>

          <div className={styles.filterContainer}>
            <label className={styles.filterLabel}>
              <FaGoogle size={16} />
              Fuente
            </label>
            <select
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">Todas las fuentes</option>
              <option value="workspace">Sincronizados de Workspace</option>
              <option value="manual">Creados manualmente</option>
            </select>
          </div>

          {allOrgUnits.length > 0 && (
            <div className={styles.filterContainer}>
              <label className={styles.filterLabel}>
                <Building size={16} />
                Unidad Organizativa
              </label>
              <select
                value={selectedOrgUnit}
                onChange={(e) => setSelectedOrgUnit(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="">Todas las unidades</option>
                {allOrgUnits.map(orgUnit => (
                  <option key={orgUnit.path} value={orgUnit.path}>
                    {orgUnit.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className={styles.filterContainer}>
            <button
              className={styles.bulkActionsToggle}
              onClick={() => setShowBulkActions(!showBulkActions)}
            >
              <Settings size={16} />
              Acciones en lote
              {selectedUsers.size > 0 && (
                <span className={styles.selectedBadge}>{selectedUsers.size}</span>
              )}
            </button>
          </div>
        </div>

        {/* Filtros activos */}
        {(selectedRole || selectedSource || selectedOrgUnit || searchQuery) && (
          <div className={styles.activeFilters}>
            <span className={styles.activeFiltersLabel}>Filtros activos:</span>
            {searchQuery && (
              <span className={styles.activeFilter}>
                <Search size={12} />
                Búsqueda: "{searchQuery}"
                <button onClick={() => setSearchQuery('')}>×</button>
              </span>
            )}
            {selectedRole && (
              <span className={styles.activeFilter}>
                <Shield size={12} />
                Rol: {getRoleName(selectedRole)}
                <button onClick={() => setSelectedRole('')}>×</button>
              </span>
            )}
            {selectedSource && (
              <span className={styles.activeFilter}>
                <FaGoogle size={12} />
                Fuente: {
                  selectedSource === 'workspace' ? 'Workspace' : 
                  selectedSource === 'manual' ? 'Manual' : 
                  'Disponibles'
                }
                <button onClick={() => setSelectedSource('')}>×</button>
              </span>
            )}
            {selectedOrgUnit && (
              <span className={styles.activeFilter}>
                <Building size={12} />
                OU: {allOrgUnits.find(ou => ou.path === selectedOrgUnit)?.name || selectedOrgUnit}
                <button onClick={() => setSelectedOrgUnit('')}>×</button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Acciones en lote */}
      {showBulkActions && (
        <div className={styles.bulkActions}>
          <div className={styles.bulkHeader}>
            <h4 className={styles.bulkTitle}>
              <Settings size={18} />
              Acciones en Lote
            </h4>
            <span className={styles.selectedCount}>
              {selectedUsers.size} usuarios seleccionados
            </span>
          </div>
          
          <div className={styles.bulkSelectionActions}>
            <button onClick={() => toggleAllUsers(true)}>
              <UserCheck size={16} />
              Seleccionar página actual ({currentUsers.length})
            </button>
            <button onClick={() => toggleAllUsers(false)}>
              <UserX size={16} />
              Deseleccionar página
            </button>
            <button onClick={() => setSelectedUsers(new Set())}>
              <RefreshCw size={16} />
              Limpiar selección
            </button>
          </div>
          
          <div className={styles.bulkActionButtons}>
            <button
              className={styles.roleChangeButton}
              onClick={() => setShowBulkRoleModal(true)}
              disabled={selectedUsers.size === 0}
            >
              <Shield size={16} />
              Cambiar rol ({selectedUsers.size})
            </button>
            <button
              className={styles.deleteButton}
              onClick={bulkDeleteUsers}
              disabled={selectedUsers.size === 0}
            >
              <Trash2 size={16} />
              Eliminar de BD ({selectedUsers.size})
            </button>
          </div>
        </div>
      )}

      {/* Tabla de usuarios */}
      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <div className={styles.headerButtons}>
            <button 
              className={`${styles.workspaceButton} ${showWorkspaceSync ? styles.active : ''}`}
              onClick={() => setShowWorkspaceSync(true)}
            >
              <FaGoogle size={16} />
              <span>Workspace</span>
              {stats.availableInWorkspace > 0 && (
                <span className={styles.notificationBadge}>{stats.availableInWorkspace}</span>
              )}
            </button>
            <button 
              className={styles.createButton}
              onClick={() => setShowCreateUser(true)}
            >
              <Plus size={16} />
              <span>Crear Usuario</span>
            </button>
          </div>
          <div className={styles.tableActions}>
            <span className={styles.showingResults}>
              Mostrando {indexOfFirstUser + 1}-{Math.min(indexOfLastUser, filteredUsers.length)} de {filteredUsers.length}
            </span>
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>
                  <input
                    type="checkbox"
                    checked={currentUsers.length > 0 && currentUsers.every(user => selectedUsers.has(user.id))}
                    onChange={(e) => toggleAllUsers(e.target.checked)}
                  />
                </th>
                <th className={styles.th}>Usuario</th>
                <th className={styles.th}>Rol</th>
                <th className={styles.th}>Fuente</th>
                <th className={styles.th}>Unidad Org.</th>
                <th className={styles.th}>Estado</th>
                <th className={styles.th}>Último acceso</th>
                <th className={styles.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.length > 0 ? (
                currentUsers.map((user) => (
                  <tr 
                    key={user.id} 
                    className={`
                      ${isCurrentUser(user.id) ? styles.currentUserRow : ''} 
                      ${user.suspended ? styles.suspendedRow : ''} 
                      ${selectedUsers.has(user.id) ? styles.selectedRow : ''}
                      ${user.isWorkspaceAvailable && !user.syncedFromWorkspace ? styles.availableRow : ''}
                    `}
                  >
                    <td className={styles.td}>
                      <input
                        type="checkbox"
                        checked={selectedUsers.has(user.id)}
                        onChange={() => toggleUserSelection(user.id)}
                      />
                    </td>
                    <td className={styles.td}>
                      <div className={styles.userCell}>
                        <div className={styles.userAvatar}>
                          <div className={styles.avatarPlaceholder}>
                            {(user.firstName?.[0] || '').toUpperCase()}
                            {(user.lastName?.[0] || '').toUpperCase()}
                          </div>
                        </div>
                        <div className={styles.userInfo}>
                          <div className={styles.userName}>
                            {user.firstName} {user.lastName}
                            {isCurrentUser(user.id) && (
                              <Shield size={14} className={styles.currentUserIcon} />
                            )}
                            {user.workspaceData?.isAdmin && (
                              <span className={styles.workspaceAdminBadge}>
                                <Shield size={12} />
                                Admin WS
                              </span>
                            )}
                          </div>
                          <div className={styles.userEmail} title={user.id}>
                            <Mail size={12} />
                            {user.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={styles.td}>
                      <span className={`${styles.badge} ${user.role === "admin" ? styles.adminBadge : styles.userBadge}`}>
                        {getRoleName(user.role)}
                      </span>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.sourceCell}>
                        {user.syncedFromWorkspace ? (
                          <span className={styles.workspaceBadge}>
                            <FaGoogle size={12} />
                            Workspace
                          </span>
                        ) : user.isWorkspaceAvailable ? (
                          <span className={styles.availableSourceBadge}>
                            <FaGoogle size={12} />
                            Disponible
                          </span>
                        ) : (
                          <span className={styles.manualBadge}>
                            Manual
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={styles.td}>
                      {user.orgUnit ? (
                        <div className={styles.orgUnitCell}>
                          <span className={styles.orgUnitName}>
                            <Building size={12} />
                            {user.orgUnitName || user.orgUnit.split('/').pop()}
                          </span>
                          <span className={styles.orgUnitPath} title={user.orgUnit}>
                            {user.orgUnit}
                          </span>
                        </div>
                      ) : (
                        <span className={styles.noOrgUnit}>—</span>
                      )}
                    </td>
                    <td className={styles.td}>
                      <div className={styles.statusCell}>
                        {user.suspended ? (
                          <span className={styles.statusSuspended}>
                            <AlertTriangle size={12} />
                            Suspendido
                          </span>
                        ) : user.isWorkspaceAvailable && !user.syncedFromWorkspace ? (
                          <span className={styles.statusAvailable}>
                            <Download size={12} />
                            Disponible
                          </span>
                        ) : (
                          <span className={styles.statusActive}>
                            <CheckCircle size={12} />
                            Activo
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.lastLoginCell}>
                        {user.workspaceData?.lastLoginTime ? (
                          <>
                            <Clock size={12} />
                            <span>{formatRelativeTime(user.workspaceData.lastLoginTime)}</span>
                          </>
                        ) : (
                          <span className={styles.noLogin}>Sin registro</span>
                        )}
                      </div>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.actions}>
                        {!user.isWorkspaceAvailable && (
                          <button
                            className={`${styles.actionButton} ${styles.editButton}`}
                            onClick={() => setEditingUser(user)}
                            title="Editar usuario"
                          >
                            <Edit2 size={16} />
                          </button>
                        )}
                        
                        {!user.isWorkspaceAvailable && (
                          <button
                            className={`${styles.actionButton} ${styles.deleteButton} ${!canDeleteUser(user.id) ? styles.disabledButton : ''}`}
                            onClick={() => setUserToDelete(user)}
                            disabled={!canDeleteUser(user.id)}
                            title={!canDeleteUser(user.id) ? "No puedes eliminar tu propia cuenta" : "Eliminar de la base de datos"}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className={styles.emptyState}>
                    <div className={styles.emptyStateContent}>
                      <Users size={48} />
                      <h4>No se encontraron usuarios</h4>
                      <p>No hay usuarios que coincidan con los filtros aplicados</p>
                      {(selectedRole || selectedSource || selectedOrgUnit || searchQuery) && (
                        <button
                          className={styles.clearFiltersButton}
                          onClick={() => {
                            setSearchQuery('');
                            setSelectedRole('');
                            setSelectedSource('');
                            setSelectedOrgUnit('');
                          }}
                        >
                          <RefreshCw size={16} />
                          Limpiar filtros
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {renderPagination()}

      {/* Modales */}
      <UserModals
        showDeleteModal={!!userToDelete}
        userToDelete={userToDelete}
        showBulkRoleModal={showBulkRoleModal}
        bulkRoleChange={bulkRoleChange}
        selectedUsersCount={selectedUsers.size}
        roles={roles}
        getRoleName={getRoleName}
        onDeleteConfirm={async () => {
          if (!userToDelete) return;

          if (!canDeleteUser(userToDelete.id)) {
            showFeedback("error", "No puedes eliminar tu propia cuenta");
            setUserToDelete(null);
            return;
          }

          try {
            if (!userToDelete.isWorkspaceAvailable) {
              await deleteDoc(doc(db, "users", userToDelete.id));
              showFeedback("success", "Usuario eliminado de la base de datos");
            } else {
              showFeedback("error", "Este usuario solo está disponible en Workspace, no en la base de datos local");
            }
            onUpdate();
          } catch (error) {
            showFeedback("error", "Error al eliminar usuario: " + error.message);
          } finally {
            setUserToDelete(null);
          }
        }}
        onDeleteCancel={() => setUserToDelete(null)}
        onBulkRoleChange={handleBulkRoleChange}
        onBulkRoleCancel={() => {
          setShowBulkRoleModal(false);
          setBulkRoleChange('');
        }}
        setBulkRoleChange={setBulkRoleChange}
      />

      {/* Modal para crear/editar usuario */}
      <ModalWrapper 
        isOpen={showCreateUser || !!editingUser} 
        onClose={() => {
          setShowCreateUser(false);
          setEditingUser(null);
        }}
        className="large"
        fullscreenOnMobile={true}  // ← Añade esta línea
      >
        <CreateUserForm 
          roles={roles}
          editingUser={editingUser}
          currentUser={currentUser}
          onCancel={() => {
            setShowCreateUser(false);
            setEditingUser(null);
          }}
          onSuccess={() => {
            if (editingUser) {
              handleEditSuccess();
            } else {
              handleCreateSuccess();
            }
          }}
          showFeedback={showFeedback}
        />
      </ModalWrapper>

    </div>
  );
};

export default UserManagement;